import {format, parseISO} from "date-fns";

/**
 * Calculates total number of months between two date objects.
 *
 * @export
 * @param {Date} startDate
 * @param {Date} endDate
 * @return {number} total months
 */
export function getMonthDifference(startDate: Date, endDate: Date): number {
	return (
		endDate.getMonth() -
		startDate.getMonth() +
		12 * (endDate.getFullYear() - startDate.getFullYear())
	);
}

/**
 * Convert the date format to a shorter version. ex: Sep 2021.
 *
 * @export
 * @param {string | Date} dateString
 * @return {string} formatted date
 */
export const convertDateToShortMonthFullYearFormat = (dateString: string | Date): string => {
	if (!dateString) return "";
	const date = typeof dateString === "string" ? new Date(dateString) : dateString;

	return date.toLocaleString("en-US", {year: "numeric", month: "short"});
};

/**
 * Given the total number of months, it returns a humanized string.
 *
 * @export
 * @param {number} months total number of months
 * @return {string} humanized string "1 yrs 7 months"
 */
export function humanizeMonths(months: number): string {
	const years = Math.floor(months / 12);

	return years ? `${years} yrs ${months - years * 12} months` : `${months} months`;
}

/**
 * Converts an ISO date string to a standard date format (dd.MM.yyyy).
 *
 * @export
 * @param {string} date ISO date string
 * @return {string} formatted date
 */
export function getStandardDateFormatFromIso(date: string): string {
	const d = parseISO(date);

	return format(d, "dd.MM.yyyy");
}

/**
 * Returns the given ISO date string in the format dd.MM.yyyy HH:mm.
 *
 * If no ISO date string is provided, the current date is used.
 *
 * @param {string} [isoDateString] Optional ISO date string
 * @return {string} The given date in the format dd.MM.yyyy HH:mm.
 */

export function getDateInStandardFormat(isoDateString?: string): string {
	const currentDate = isoDateString ? new Date(isoDateString) : new Date();

	// Replace slashes with dots
	return currentDate
		.toLocaleString("en-GB", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			hour12: false, // Use 24-hour format
		})
		.replace(/\//g, ".")
		.replace(",", ""); // Output: 14.01.2025 14:00
}

/**
 * Returns the current date and time in ISO 8601 format (yyyy-MM-dd'T'HH:mm:ss.SSS'Z').
 *
 * @returns {string} Current date and time in ISO 8601 format.
 */

export function getCurrentDateInISOFormat(): string {
	// Get the current date and time
	const currentDate = new Date();

	// Convert to ISO 8601 format
	return currentDate.toISOString(); // Output: 2025-02-25T16:30:44.000Z (example)
}

export function getAltStandardDateFormatFromIso(date: string) {
	const d = parseISO(date);

	return format(d, "MMM yyyy");
}
